import React, { useState } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import MUIDataTable from "mui-datatables";
import { Backdrop, Button, CircularProgress, createTheme } from "@mui/material";
import { ThemeProvider, withStyles } from "@mui/styles";
import BasicDateRangePicker from "../../../components/common/datepicker";
import ConsumerWalletTable from "./Table/ConsumerWallet";
import ManualWalletTransaction from "./Table/ManualWalletTab";
import { BASE_URL } from "../../../components/config/config";
import {
  canViewWallet,
  canViewCenterTransactions,
  canViewConsumerTransactions,
  canViewUserTransaction,
  canViewClientTransaction,
  canClientRecharge,
  canConsumerTechnicianRecharge,
} from "../../../utility/permissionUtils";

const theme = createTheme({
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: "red",
        },
        indicator: {
          backgroundColor: "red",
        },
      },
    },
  },
});

const CustomTab = withStyles({
  root: {
    textTransform: "none",
    color: "white",
    fontWeight: "500",
    fontSize: "18px",
    textTransform: "capitalize",
    letterSpacing: "5px",
  },
})(Tab);

// Mui data table
const columns = [
  {
    name: "createdAt",
    label: "Transaction Date",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value) => moment(value).format("Do MMM, hh:ss A"),
    },
  },
  {
    name: "userId",
    label: "User ID",
    options: {
      filter: true,
      sort: false,
    },
  },
  {
    name: "center_INA",
    label: "Center INA",
    options: {
      filter: true,
      sort: false,
    },
  },
  {
    name: "center_name",
    label: "Center Name",
    options: {
      filter: true,
      sort: false,
    },
  },
  {
    name: "center_mobile",
    label: "Center Number",
    options: {
      filter: true,
      sort: false,
      display: "none",
    },
  },
  {
    name: "amount",
    label: "Total Amount",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "totalTransactions",
    label: "Total Transactions",
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: "wallet_remain",
    label: "Balance",
    options: {
      filter: false,
      sort: true,
    },
  },
];

const userTransactionColumns = [
  {
    name: "createdAt",
    label: "Transaction Date",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value) => moment(value).format("Do MMM, hh:ss A"),
    },
  },
  {
    name: "center_INA",
    label: "Center INA",
    options: {
      filter: true,
      sort: false,
    },
  },
  {
    name: "center_name",
    label: "Center Name",
    options: {
      filter: true,
      sort: false,
    },
  },
  {
    name: "transactionId",
    label: "Transaction ID",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "amount",
    label: "Amount",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "wallet_remain",
    label: "Remaining Balance",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "status",
    label: "Status",
    options: {
      filter: false,
      sort: false,
    },
  },
];

const options = {
  selectableRows: "none",
  responsive: "standard",
  filterType: "checkbox",
  filter: true,
  textLabels: {
    filter: "Filter",
    reset: "Reset",
    close: "Close",
  },
  customFilterDialogFooter: (currentFilterList, applyNewFilters) => (
    <div style={{ padding: "20px" }}>
      <Button variant="contained" onClick={() => applyNewFilters()}>
        Apply
      </Button>
    </div>
  ),
  customFilterDialogFooterStyle: {
    borderTop: "1px solid #ccc",
  },
  customFilterDialogFooterPosition: "right",
  customFilterDialogWidth: "500px",
};

const stylesCss = {
  inputBox: {
    width: "100%",
    marginBottom: "10px",
  },
  gridStyle: {
    width: "80%",
    margin: "auto",
  },
  paddingInnerGrids: {
    paddingRight: "10px",
    paddingLeft: "10px",
    paddingTop: "2px",
  },
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function WalletHistory() {
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  const [loader, setLoader] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState(null);
  const [userTransactions, setUserTransactions] = React.useState([]);
  const [accessToken, setAccessToken] = React.useState("");
  const [rows, setRows] = useState([]);

  const getInitialTab = () => {
    const savedTab = localStorage.getItem("walletHistorySelectedTab");
    return savedTab ? parseInt(savedTab) : 0;
  };
  const [value, setValue] = useState(getInitialTab());

  const canViewManualTab =
    canViewUserTransaction(userPermissions) ||
    canViewClientTransaction(userPermissions) ||
    canClientRecharge(userPermissions) ||
    canConsumerTechnicianRecharge(userPermissions);

  // Filter tabs based on permissions
  const getAvailableTabs = () => {
    const tabs = [];

    if (canViewCenterTransactions(userPermissions)) {
      tabs.push({ label: "CENTER", index: 0 });
    }

    if (canViewConsumerTransactions(userPermissions)) {
      tabs.push({ label: "CONSUMER", index: 1 });
    }

    if (canViewManualTab) {
      tabs.push({ label: "MANUAL TRANSACTION", index: 2 });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  // Adjust initial tab if current tab is not available
  React.useEffect(() => {
    const currentTabAvailable = availableTabs.some(
      (tab) => tab.index === value
    );
    if (!currentTabAvailable && availableTabs.length > 0) {
      setValue(availableTabs[0].index);
    }
  }, [userPermissions]);

  const refreshData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}admin/wallet-transactions`, {
        headers: {
          Authorization: accessToken,
        },
      });
      setRows(response.data?.data);
    } catch (error) {
      console.log("Error fetching data:", error);
      return [];
    }
  };

  const onClickFilter = async (value) => {
    let payload = {
      start_date: value[0],
      end_date: value[1],
    };

    try {
      const response = await axios.get(`${BASE_URL}admin/wallet-transactions`, {
        headers: {
          Authorization: accessToken,
        },
        params: payload,
      });
      setRows(response.data?.data);
    } catch (error) {
      console.log("Error fetching data:", error);
      return [];
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    localStorage.setItem("walletHistorySelectedTab", newValue.toString());
  };

  React.useEffect(() => {
    setAccessToken(globalState?.userTokens?.accessToken);
    if (canViewCenterTransactions(userPermissions)) {
      fetchData();
    }
  }, [globalState, userPermissions]);

  const fetchData = async () => {
    if (!canViewCenterTransactions(userPermissions)) return;

    try {
      setLoader(true);
      const response = await axios.get(`${BASE_URL}admin/wallet-transactions`, {
        headers: {
          Authorization: accessToken,
        },
      });

      const sortedData = response.data?.data.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      const groupedRows = sortedData.reduce((acc, transaction) => {
        const { userId, amount } = transaction;
        if (!acc[userId]) {
          acc[userId] = { ...transaction, amount: 0, totalTransactions: 0 };
        }
        acc[userId].amount += amount;
        acc[userId].totalTransactions++;
        return acc;
      }, {});

      const finalRows = Object.values(groupedRows);
      setRows(finalRows);
    } catch (error) {
      console.log("Error fetching data:", error);
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  const handleRowClick = (userId) => {
    setSelectedUserId(userId);
    fetchUserTransactions(userId);

    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
  };

  const fetchUserTransactions = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}admin/wallet-transactions-byId`,
        {
          headers: {
            Authorization: accessToken,
          },
          params: {
            userId: userId,
          },
        }
      );

      const sortedTransactions = response.data?.data.sort((a, b) => {
        return (
          new Date(b?.createdAt).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          }),
          -new Date(a?.createdAt).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          })
        );
      });

      setUserTransactions(response.data?.data);
    } catch (error) {
      console.log("Error fetching user transactions:", error);
      setUserTransactions([]);
    }
  };

  const handleCloseDetailView = () => {
    setSelectedUserId(null);
    setUserTransactions([]);
  };

  // Check if user has any transaction permissions
  const hasTransactionAccess = canViewWallet(userPermissions);

  if (!hasTransactionAccess) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No permission to view transaction details. Please contact
          administrator.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <ThemeProvider theme={theme}>
        <Typography variant="h4" mb={2} textAlign="center">
          Transaction History
        </Typography>

        <Box sx={{ border: 1, borderColor: "divider", width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              textColor="white"
              aria-label="secondary tabs example"
              sx={{
                "& .MuiTabs-flexContainer": {
                  justifyContent: "space-around",
                  background: "#9DB2BF",
                },
                "& .MuiTabs-scroller": {
                  "&::before": {
                    content: '""',
                    display: "block",
                    flexShrink: 0,
                  },
                },
              }}
            >
              {availableTabs.map((tab) => (
                <CustomTab
                  key={tab.index}
                  label={tab.label}
                  {...a11yProps(tab.index)}
                />
              ))}
            </Tabs>
          </Box>

          {canViewCenterTransactions(userPermissions) && (
            <TabPanel value={value} index={0}>
              <Box style={stylesCss.paddingInnerGrids}>
                <BasicDateRangePicker
                  onClickFilter={onClickFilter}
                  refreshData={refreshData}
                />
              </Box>
              <MUIDataTable
                data={rows}
                columns={columns}
                options={{ ...options, onRowClick: handleRowClick }}
              />
              {selectedUserId && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "right",
                    }}
                  >
                    <Typography variant="h6">
                      {userTransactions.length > 0
                        ? userTransactions[0].center_name
                        : ""}
                    </Typography>
                    <Button variant="contained" onClick={handleCloseDetailView}>
                      Close
                    </Button>
                  </div>
                  <MUIDataTable
                    data={userTransactions}
                    columns={userTransactionColumns}
                    options={options}
                    id={`user-table-${selectedUserId}`}
                  />
                </div>
              )}
            </TabPanel>
          )}

          {canViewConsumerTransactions(userPermissions) && (
            <TabPanel value={value} index={1}>
              <ConsumerWalletTable userPermissions={userPermissions} />
            </TabPanel>
          )}

          {canViewManualTab && (
            <TabPanel value={value} index={2}>
              <ManualWalletTransaction userPermissions={userPermissions} />
            </TabPanel>
          )}
        </Box>
      </ThemeProvider>
    </>
  );
}
